import { GitMerge } from 'lucide-react'
import { authorForByline, authorSlugPath } from '../../lib/authors'
import { contentPath, type ContentEntry } from '../../lib/content'

// The blog index card for the PR #7536 story, drawn as the merged pull request itself. Self-contained on purpose:
// it must not import story-content.ts, so the story's copy stays off the blog index bundle.
const ADDED = 4_460_070
const REMOVED = 3_404_404
const DIFF: { sign: ' ' | '-' | '+'; path: string; note: string }[] = [
  { sign: ' ', path: 'langwatch/', note: '' },
  { sign: '-', path: '  platform/', note: 'one app, 17,024 files' },
  { sign: '+', path: '  modules/', note: '66 modules' },
  { sign: '+', path: '    agent/ billing/ trace/ …', note: 'contract · process' },
  { sign: '+', path: '  apps/', note: 'api · worker · tasks · ui' },
]
const fmt = (n: number) => n.toLocaleString('en-GB')

/** GitHub's five-square diffstat: added and removed lines, rounded to five squares. */
function DiffBar() {
  const green = Math.round((ADDED / (ADDED + REMOVED)) * 5)
  return (
    <span className="inline-flex gap-[2px]" aria-hidden>
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} className={`h-2 w-2 rounded-[2px] ${i < green ? 'bg-[#1a7f37]' : 'bg-[#cf222e]'}`} />
      ))}
    </span>
  )
}

export function CommitCard({ entry }: { entry: ContentEntry }) {
  const author = authorForByline(entry.meta.author)
  return (
    <div className="relative top-0 flex flex-col transition-[top] duration-300 ease-out hover:-top-1.5">
      <a href={contentPath(entry)} aria-label={entry.meta.title} className="absolute inset-0 z-0" />
      <div className="pointer-events-none relative flex flex-1 flex-col overflow-hidden rounded-2xl border border-ink-100 bg-paper-soft">
        <div className="p-6 pb-4">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#8250df] px-2.5 py-1 text-[12px] font-semibold text-white">
              <GitMerge className="h-3.5 w-3.5" strokeWidth={2.4} />
              Merged
            </span>
            <span className="font-mono text-[13px] text-ink-400">#7536</span>
          </div>
          <h2 className="mt-3 text-[19px] font-semibold leading-[1.3] text-ink-900">{entry.meta.title}</h2>
          <div className="relative z-10 mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-ink-500">
            {author?.photo ? <img src={author.photo} alt="" aria-hidden="true" loading="lazy" className="h-5 w-5 rounded-full object-cover" /> : null}
            {author ? (
              <a href={authorSlugPath(author)} className="pointer-events-auto relative z-10 whitespace-nowrap font-medium text-ink-700 hover:text-ink-900 hover:underline">
                {entry.meta.author}
              </a>
            ) : (
              <span className="font-medium text-ink-700">{entry.meta.author}</span>
            )}
            <span>merged 9,187 commits into <span className="rounded-md bg-ink-50 px-1.5 py-0.5 font-mono text-[11.5px] text-[#0969da]">main</span></span>
          </div>
        </div>

        <div className="mx-6 overflow-hidden rounded-lg border border-ink-100 bg-paper font-mono text-[11.5px] leading-[1.75]">
          {DIFF.map((row) => (
            <div
              key={row.path}
              className={`flex gap-2 px-3 ${row.sign === '+' ? 'bg-[#dafbe1] text-[#116329]' : row.sign === '-' ? 'bg-[#ffebe9] text-[#82071e]' : 'text-ink-500'}`}
            >
              <span className="w-2 shrink-0 select-none">{row.sign === ' ' ? '' : row.sign}</span>
              <span className="min-w-0 flex-1 truncate whitespace-pre">{row.path}</span>
              <span className="hidden shrink-0 opacity-70 sm:inline">{row.note}</span>
            </div>
          ))}
        </div>

        <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 p-6 pt-5 font-mono text-[12px] text-ink-500">
          <span>45,995 files</span>
          <span className="text-[#1a7f37]">+{fmt(ADDED)}</span>
          <span className="text-[#cf222e]">−{fmt(REMOVED)}</span>
          <DiffBar />
        </div>
        <div className="-mt-4 px-6 pb-6 text-[12.5px] text-ink-500">30 merges from main over 51 days</div>
      </div>
    </div>
  )
}
